// Per-agent health helpers (#3039) shared by the badge, the table and the
// map. Mirrors services/hosted_agents/health.py — the STATE is computed by
// the backend; this file only decides how to show it.

import type { HostedAgentHealthState } from "@/api/admin-api"

/** English fallbacks (the locale files carry hostedAgents.health.states.*). */
export const HOSTED_AGENT_HEALTH_STATE_DEFAULTS: Record<HostedAgentHealthState, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  unreachable: "Unreachable",
  never_seen: "Never seen",
  disabled: "Disabled",
}

export type HostedAgentHealthBadgeVariant =
  | "success"
  | "warning"
  | "destructive"
  | "outline"
  | "disabled"

/** Badge colour per state: green / amber / red / neutral / muted. */
export function hostedAgentHealthVariant(
  state: HostedAgentHealthState
): HostedAgentHealthBadgeVariant {
  switch (state) {
    case "healthy":
      return "success"
    case "degraded":
      return "warning"
    case "unreachable":
      return "destructive"
    case "disabled":
      return "disabled"
    default:
      return "outline"
  }
}

/**
 * "3 minutes ago" from a second count, in the viewer's language. Uses the
 * backend's `seconds_since_contact` (computed against ITS clock) rather than
 * `Date.now()` so a skewed browser clock never shows "in 2 minutes".
 */
export function formatHostedAgentSecondsAgo(
  seconds: number | null | undefined,
  locale?: string
): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null
  const clamped = Math.max(0, Math.round(seconds))
  let value: number
  let unit: Intl.RelativeTimeFormatUnit
  if (clamped < 60) {
    value = clamped
    unit = "second"
  } else if (clamped < 3600) {
    value = Math.round(clamped / 60)
    unit = "minute"
  } else if (clamped < 86400) {
    value = Math.round(clamped / 3600)
    unit = "hour"
  } else {
    value = Math.round(clamped / 86400)
    unit = "day"
  }
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: "always" }).format(-value, unit)
  } catch {
    return `${value} ${unit}${value === 1 ? "" : "s"} ago`
  }
}

/** "v0.0.110" — Hermes sends a bare version; do not double the prefix. */
export function formatHermesVersionLabel(version: string | null | undefined): string | null {
  if (!version) return null
  const trimmed = version.trim()
  if (!trimmed) return null
  return /^v/i.test(trimmed) ? trimmed : `v${trimmed}`
}
